import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';
import { Settings, User, Lock, DollarSign, Gamepad2, Gift, ArrowDownCircle, ArrowUpCircle, Save, Globe } from 'lucide-react';

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('rules');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rules, setRules] = useState({
    deposit_rules: { min_amount: 10, max_amount: 10000, enabled: true, first_deposit_bonus_percent: 100, regular_bonus_percent: 20 },
    withdrawal_rules: { min_amount: 20, max_amount: 5000, enabled: true, fee_percent: 0, min_balance_after: 0, max_per_day: 0, max_total_per_day: 0 },
    load_rules: { min_xp: 10, max_xp: 100000, enabled: true },
    redeem_rules: { min_provider_amount: 1, max_provider_amount: 10000, enabled: true },
    bonus_rules: { first_deposit_bonus_pct: 100, signup_bonus_fp: 0, signup_bonus_sc: 0, signup_bonus_xp: 0, referral_bonus_xp: 25, enabled: true },
    // Free Play Credits conversion rules (iter 115).
    fp_rules: { enabled: true, fp_to_usd_ratio: 5.0, min_convert: 10, max_convert: 0 },
    xp_conversion: { website_currency: 'USD', provider_currency: 'USD', load_xp_per_provider_unit: 1, redeem_xp_per_provider_unit: 1 },
    referral_rules: { player_referrer_bonus_sc: 0, player_referred_bonus_sc: 0, staff_referral_enabled: true, player_referral_enabled: true, staff_commission_percent: 33 },
    support_channels: { whatsapp: '', telegram: '', messenger: '' },
  });

  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });

  // Daily FP streak drip config (iter 117). Lives in its own endpoint pair
  // under `/api/admin/fp/streak` so the schedule array stays cleanly typed.
  const [streak, setStreak] = useState({
    enabled: true,
    schedule: [10, 20, 35, 50, 75, 100, 150],
    reminder_enabled: true,
    reminder_hour: 18,
  });
  const [streakSaving, setStreakSaving] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);

  useEffect(() => {
    fetchSettings();
    // Load streak config in parallel — if it fails (admin endpoint returns
    // 404 during rollout) we just keep the sensible defaults.
    api.get('/api/admin/fp/streak').then(r => {
      if (r.data) setStreak({
        enabled: !!r.data.enabled,
        schedule: r.data.schedule || [10, 20, 35, 50, 75, 100, 150],
        reminder_enabled: r.data.reminder_enabled !== false,
        reminder_hour: typeof r.data.reminder_hour === 'number' ? r.data.reminder_hour : 18,
      });
    }).catch(() => {});
  }, []);

  const saveStreak = async () => {
    setStreakSaving(true);
    try {
      await api.put('/api/admin/fp/streak', {
        enabled: streak.enabled,
        schedule: streak.schedule.map(n => Number(n) || 0),
        reminder_enabled: streak.reminder_enabled,
        reminder_hour: Number(streak.reminder_hour) || 18,
      });
      toast.success('Daily FP streak config saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save streak config');
    } finally {
      setStreakSaving(false);
    }
  };

  const sendStreakReminders = async (dryRun = false) => {
    setReminderSending(true);
    try {
      const r = await api.post(`/api/admin/fp/streak/send-reminders?dry_run=${dryRun ? 'true' : 'false'}`);
      const d = r.data || {};
      const msg = dryRun
        ? `Dry run — ${d.sent || 0} players would be reminded (${d.candidates || 0} candidates)`
        : `Sent ${d.sent || 0}, failed ${d.failed || 0}, skipped ${d.skipped || 0}`;
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setReminderSending(false);
    }
  };

  const updateStreakDay = (idx, value) => {
    setStreak(prev => {
      const next = [...prev.schedule];
      next[idx] = value;
      return { ...prev, schedule: next };
    });
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/admin/settings').catch(() => ({ data: {} }));
      const s = res.data.settings || res.data || {};
      setRules(prev => {
        const merged = {
          ...prev,
          ...s,
          deposit_rules: { ...(prev.deposit_rules || {}), ...(s.deposit_rules || {}) },
          withdrawal_rules: { ...(prev.withdrawal_rules || {}), ...(s.withdrawal_rules || {}) },
          load_rules: { ...(prev.load_rules || {}), ...(s.load_rules || {}) },
          redeem_rules: { ...(prev.redeem_rules || {}), ...(s.redeem_rules || {}) },
          bonus_rules: { ...(prev.bonus_rules || {}), ...(s.bonus_rules || {}) },
          fp_rules: { ...(prev.fp_rules || {}), ...(s.fp_rules || {}) },
          referral_rules: { ...(prev.referral_rules || {}), ...(s.referral_rules || {}) },
          support_channels: { ...(prev.support_channels || {}), ...(s.support_channels || {}) },
        };
        // Canonical signup bonus is FP. Mirror legacy fields so old saved settings
        // and older frontend/backend builds still resolve the same value.
        const br = { ...(merged.bonus_rules || {}) };
        const signupBonusFp = Number(br.signup_bonus_fp ?? br.signup_bonus_sc ?? br.signup_bonus_xp ?? 0);
        br.signup_bonus_fp = signupBonusFp;
        br.signup_bonus_sc = signupBonusFp;
        br.signup_bonus_xp = signupBonusFp;
        return { ...merged, bonus_rules: br };
      });
    } catch {} finally { setLoading(false); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      // Keep legacy signup bonus fields in sync with canonical FP setting so
      // older readers continue to work without crediting wallet cash.
      const payload = { ...rules };
      if (payload.bonus_rules) {
        const signupBonusFp = Number(payload.bonus_rules.signup_bonus_fp ?? payload.bonus_rules.signup_bonus_sc ?? payload.bonus_rules.signup_bonus_xp ?? 0);
        payload.bonus_rules = {
          ...payload.bonus_rules,
          signup_bonus_fp: signupBonusFp,
          signup_bonus_sc: signupBonusFp,
          signup_bonus_xp: signupBonusFp,
        };
      }
      await api.put('/api/admin/settings', payload);
      toast.success('Platform rules saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error('Passwords do not match'); return; }
    try {
      await api.post('/api/auth/change-password', { old_password: passwordData.current_password, new_password: passwordData.new_password });
      toast.success('Password changed');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateRule = (section, key, value) => {
    setRules(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const tabs = [
    { id: 'rules', label: 'Platform Rules', icon: Settings },
    { id: 'signup', label: 'Signup Bonus', icon: Gift },
    { id: 'deposit', label: 'Deposit Rules', icon: ArrowDownCircle },
    { id: 'withdrawal', label: 'Withdrawal Rules', icon: ArrowUpCircle },
    { id: 'bonus', label: 'Bonus Rules', icon: Gift },
    { id: 'fp', label: 'Free Play Credits', icon: Gift },
    { id: 'referral', label: 'Referral Rules', icon: User },
    { id: 'support', label: 'Support Channels', icon: Globe },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="settings-page">
      <h2 className="text-xl font-semibold">Platform Settings</h2>

      <div className="flex gap-1 bg-[#131822] rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap ${activeTab === t.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : (
        <>
          {activeTab === 'rules' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">General Load/Redeem Rules</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Load ($)" value={rules.load_rules.min_xp} onChange={v => updateRule('load_rules', 'min_xp', v)} />
                <RuleInput label="Max Load ($)" value={rules.load_rules.max_xp} onChange={v => updateRule('load_rules', 'max_xp', v)} />
                <RuleInput label="Min Redeem (Provider $)" value={rules.redeem_rules.min_provider_amount} onChange={v => updateRule('redeem_rules', 'min_provider_amount', v)} />
                <RuleInput label="Max Redeem (Provider $)" value={rules.redeem_rules.max_provider_amount} onChange={v => updateRule('redeem_rules', 'max_provider_amount', v)} />
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-rules-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Rules'}
              </Button>
            </div>
          )}

          {activeTab === 'signup' && (
            <div className="glass-card rounded-xl p-5 space-y-4" data-testid="signup-bonus-settings">
              <div>
                <h3 className="font-semibold">First-Time Signup Bonus</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Credits Free Play (FP) to new players after successful registration. Set 0 to disable. This never adds withdrawable wallet cash.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput
                  label="Signup Bonus (FP)"
                  value={rules.bonus_rules.signup_bonus_fp}
                  onChange={v => updateRule('bonus_rules', 'signup_bonus_fp', v)}
                  testid="bonus-signup-fp"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Signup Bonus Enabled</label>
                <button onClick={() => updateRule('bonus_rules', 'enabled', !rules.bonus_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.bonus_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  data-testid="signup-bonus-enabled-toggle">
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.bonus_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-signup-bonus-rules">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Signup Bonus'}
              </Button>
            </div>
          )}

          {activeTab === 'deposit' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Deposit Rules</h3>
              <p className="text-xs text-gray-500">
                First deposit bonus is always paid as FP. Referral earnings are configured separately and credit real cash to the referrer's main balance.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Deposit ($)" value={rules.deposit_rules.min_amount} onChange={v => updateRule('deposit_rules', 'min_amount', v)} />
                <RuleInput label="Max Deposit ($)" value={rules.deposit_rules.max_amount} onChange={v => updateRule('deposit_rules', 'max_amount', v)} />
                <RuleInput label="First Deposit Bonus %" value={rules.deposit_rules.first_deposit_bonus_percent} onChange={v => updateRule('deposit_rules', 'first_deposit_bonus_percent', v)} />
                <RuleInput label="Regular Deposit Bonus %" value={rules.deposit_rules.regular_bonus_percent} onChange={v => updateRule('deposit_rules', 'regular_bonus_percent', v)} />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Deposits Enabled</label>
                <button onClick={() => updateRule('deposit_rules', 'enabled', !rules.deposit_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.deposit_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.deposit_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'withdrawal' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Withdrawal Rules ($ wallet)</h3>
              <p className="text-xs text-gray-500">
                Limits apply to $ wallet withdrawals only. Game-provider redeem caps are configured separately.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Withdrawal ($)" value={rules.withdrawal_rules.min_amount} onChange={v => updateRule('withdrawal_rules', 'min_amount', v)} />
                <RuleInput label="Max Withdrawal per Request ($)" value={rules.withdrawal_rules.max_amount} onChange={v => updateRule('withdrawal_rules', 'max_amount', v)} />
                <RuleInput label="Withdrawal Fee %" value={rules.withdrawal_rules.fee_percent} onChange={v => updateRule('withdrawal_rules', 'fee_percent', v)} />
                <RuleInput label="Min Balance After ($)" value={rules.withdrawal_rules.min_balance_after} onChange={v => updateRule('withdrawal_rules', 'min_balance_after', v)} />
                <RuleInput
                  label="Max Withdrawals per Day (0 = unlimited)"
                  value={rules.withdrawal_rules.max_per_day}
                  onChange={v => updateRule('withdrawal_rules', 'max_per_day', v)}
                  testid="withdrawal-max-per-day"
                />
                <RuleInput
                  label="Max Daily Withdrawal Total ($, 0 = unlimited)"
                  value={rules.withdrawal_rules.max_total_per_day}
                  onChange={v => updateRule('withdrawal_rules', 'max_total_per_day', v)}
                  testid="withdrawal-max-total-per-day"
                />
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-withdrawal-rules"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'bonus' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Bonus Rules</h3>
              <p className="text-xs text-gray-500">
                Deposit bonuses apply automatically as Free Play (FP) credits only. First-time signup FP is configured in the Signup Bonus tab. Bonuses and rewards never add withdrawable wallet cash.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="First Deposit Bonus %" value={rules.bonus_rules.first_deposit_bonus_pct} onChange={v => updateRule('bonus_rules', 'first_deposit_bonus_pct', v)} testid="bonus-first-dep-pct" />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Bonuses Enabled</label>
                <button onClick={() => updateRule('bonus_rules', 'enabled', !rules.bonus_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.bonus_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  data-testid="bonus-enabled-toggle">
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.bonus_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-bonus-rules"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'fp' && (
            <div className="glass-card rounded-xl p-5 space-y-4" data-testid="fp-rules-card">
              <h3 className="font-semibold">Free Play Credits</h3>
              <p className="text-xs text-gray-400">
                Players earn FP Credits from tasks, rewards, and promos. FP spends 1:1 inside games
                but is NOT directly withdrawable — players must first convert FP → Wallet $ at the
                ratio you set below. Example: 10 FP converts to $2 when the ratio is 5 FP per $1.
              </p>
              <p className="text-xs text-[#F5D76E]">
                Current effective value: {Math.round((1 / (Number(rules.fp_rules.fp_to_usd_ratio) || 5)) * 100)}% of FP converts to main balance.
                {' '}At this rate, 10 FP = ${(10 / (Number(rules.fp_rules.fp_to_usd_ratio) || 5)).toFixed(2)}.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <RuleInput label="FP per $1 (5 = 10 FP converts to $2)" value={rules.fp_rules.fp_to_usd_ratio} onChange={v => updateRule('fp_rules', 'fp_to_usd_ratio', v)} testid="fp-ratio" />
                <RuleInput label="Minimum Conversion (FP)" value={rules.fp_rules.min_convert} onChange={v => updateRule('fp_rules', 'min_convert', v)} testid="fp-min" />
                <RuleInput label="Maximum Per Day (FP, 0 = no cap)" value={rules.fp_rules.max_convert} onChange={v => updateRule('fp_rules', 'max_convert', v)} testid="fp-max" />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">FP Conversion Enabled</label>
                <button onClick={() => updateRule('fp_rules', 'enabled', !rules.fp_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.fp_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  data-testid="fp-enabled-toggle">
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.fp_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-fp-rules"><Save className="w-4 h-4 mr-2" /> Save</Button>

              {/* Daily FP streak drip — iter 117. Gives a 7-day ramp that
                  reinforces the FP economy and hooks daily return logins. */}
              <div className="mt-6 pt-6 border-t border-white/5 space-y-3" data-testid="fp-streak-config">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-white">Daily FP Streak Drip</h4>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Players earn these FP amounts on consecutive daily logins. Missing a day resets to Day 1.
                    </p>
                  </div>
                  <button
                    onClick={() => setStreak(p => ({ ...p, enabled: !p.enabled }))}
                    className={`w-12 h-6 rounded-full transition-all ${streak.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                    data-testid="fp-streak-enabled-toggle"
                    aria-label="Toggle FP streak"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-all ${streak.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {streak.schedule.map((amt, i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <label className="text-[10px] uppercase tracking-wide text-gray-500 text-center">Day {i + 1}</label>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        value={amt}
                        onChange={e => updateStreakDay(i, parseFloat(e.target.value) || 0)}
                        className="bg-[#0B0E14] border-white/10 text-center"
                        data-testid={`fp-streak-day-${i + 1}-input`}
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={saveStreak} disabled={streakSaving} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="save-fp-streak">
                  <Save className="w-4 h-4 mr-2" /> Save Streak Schedule
                </Button>

                {/* Reminder push config — fires a "don't break your streak"
                    web push around the configured UTC hour every day. */}
                <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid="fp-streak-reminder-config">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-white">Streak Reminder Push</h5>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Auto-fires a web push to players whose streak will reset tonight — raises conversion without spamming non-streak users.
                      </p>
                    </div>
                    <button
                      onClick={() => setStreak(p => ({ ...p, reminder_enabled: !p.reminder_enabled }))}
                      className={`w-12 h-6 rounded-full transition-all ${streak.reminder_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                      data-testid="fp-streak-reminder-toggle"
                      aria-label="Toggle streak reminder push"
                    >
                      <div className={`w-5 h-5 rounded-full bg-white transition-all ${streak.reminder_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400">Send at UTC hour</label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={streak.reminder_hour}
                      onChange={e => setStreak(p => ({ ...p, reminder_hour: Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)) }))}
                      className="bg-[#0B0E14] border-white/10 w-20 text-center"
                      data-testid="fp-streak-reminder-hour-input"
                    />
                    <span className="text-[11px] text-gray-500">(default 18 = 6h before midnight)</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => sendStreakReminders(true)}
                      disabled={reminderSending}
                      variant="outline"
                      className="border-white/10 text-gray-300"
                      data-testid="fp-streak-reminder-dryrun"
                    >
                      Preview Audience
                    </Button>
                    <Button
                      onClick={() => sendStreakReminders(false)}
                      disabled={reminderSending}
                      className="bg-[#D4AF37] text-black hover:bg-[#F5D76E]"
                      data-testid="fp-streak-reminder-send-now"
                    >
                      Send Reminders Now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'referral' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Referral Rules</h3>
              <p className="text-xs text-gray-400">Player referral codes start with #R1, staff referral codes start with #RA. Referrers earn 33% real cash from the referred player's first deposit. First-deposit player bonus is 100% FP.</p>
              <div className="space-y-4">
                <div className="bg-[#0B0E14] rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-medium text-[#D4AF37]">Player Referral (#R1 codes)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                      <p className="text-xs text-gray-400">Referrer earns</p>
                      <p className="text-lg font-bold text-emerald-400">33% cash</p>
                      <p className="text-[10px] text-gray-500">Credited to main balance after the first deposit.</p>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                      <p className="text-xs text-gray-400">Referred player gets</p>
                      <p className="text-lg font-bold text-[#D4AF37]">100% FP</p>
                      <p className="text-[10px] text-gray-500">First-deposit bonus only.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400">Player Referrals Enabled</label>
                    <button onClick={() => updateRule('referral_rules', 'player_referral_enabled', !rules.referral_rules.player_referral_enabled)}
                      className={`w-10 h-5 rounded-full transition-all ${rules.referral_rules.player_referral_enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-all ${rules.referral_rules.player_referral_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="bg-[#0B0E14] rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-medium text-purple-400">Staff Referral (RA codes)</h4>
                  <p className="text-[10px] text-gray-500">Agents see their referred player list and earn 33% cash from each referred player's first deposit.</p>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400">Staff Referrals Enabled</label>
                    <button onClick={() => updateRule('referral_rules', 'staff_referral_enabled', !rules.referral_rules.staff_referral_enabled)}
                      className={`w-10 h-5 rounded-full transition-all ${rules.referral_rules.staff_referral_enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-all ${rules.referral_rules.staff_referral_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Support Channels</h3>
              <p className="text-sm text-gray-400">Configure external support links. Players will see these in the chat tab for quick support.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#25D366"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z" /></svg>
                    WhatsApp Number (e.g. +1234567890)
                  </label>
                  <Input value={rules.support_channels?.whatsapp || ''} onChange={e => updateRule('support_channels', 'whatsapp', e.target.value)} placeholder="+1234567890" className="bg-[#0B0E14] border-white/10" data-testid="settings-whatsapp" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0088CC"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.37-.49 1.02-.75 3.99-1.73 6.65-2.87 7.97-3.44 3.8-1.58 4.59-1.86 5.1-1.87.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06.01.24 0 .38z" /></svg>
                    Telegram Username (e.g. @support or t.me/support)
                  </label>
                  <Input value={rules.support_channels?.telegram || ''} onChange={e => updateRule('support_channels', 'telegram', e.target.value)} placeholder="@username or t.me/link" className="bg-[#0B0E14] border-white/10" data-testid="settings-telegram" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#006AFF"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2z" /></svg>
                    Messenger (Facebook page name or URL)
                  </label>
                  <Input value={rules.support_channels?.messenger || ''} onChange={e => updateRule('support_channels', 'messenger', e.target.value)} placeholder="pagename or m.me/pagename" className="bg-[#0B0E14] border-white/10" data-testid="settings-messenger" />
                </div>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

        </>
      )}
    </div>
  );
}

function RuleInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      <Input
        type="number"
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
        placeholder="0"
        className="bg-[#0B0E14] border-white/10"
        data-testid={testid}
      />
    </div>
  );
}
